// Single source of truth for all English SEO copy emitted by lib/seo/*,
// the static-page OG route, root layout metadata, and the PWA manifest.
//
// Edit this file (NOT consuming surfaces / layouts / routes) to change
// any title, description, OG card text, JSON-LD string, manifest copy,
// or root-layout default.
//
// Placeholders use `{name}` format and are interpolated by `lib/seo/format.ts`.
// Length budgets — title <= 60, description <= 155 — are enforced at use-site
// by `truncateMeta()` / `clampLength()`; this module holds raw templates.
//
// Surfaces that read from next-intl translation namespaces (gift, thesis,
// trade, proposal-back, support-mainnet hero tagline) are intentionally NOT
// migrated here — they're user-facing UI flows shown in the visitor's locale,
// not crawler-only metadata.

import type { SeoCopyShape } from './types';

const copy: SeoCopyShape = {
  site: {
    name: 'Morfi Markets',
    shortName: 'Morfi',
    description:
      'Markets for every debate. Morfi is the opinion-market platform — trade your view on the subjective debates no oracle can resolve.',
    twitterHandle: '@morfimarkets',
    titleSuffix: ' | Morfi',
  },

  rootLayout: {
    titleDefault: 'Morfi Markets',
    description:
      'Opinion markets for every debate. Prediction markets ask "will X?" — Morfi asks "should X?". Trade your view on Morfi.',
    applicationName: 'Morfi Markets',
    creator: 'Morfi Markets',
    publisher: 'Morfi Markets',
    keywords: [
      'opinion markets',
      'opinion market platform',
      'what are opinion markets',
      'opinion markets vs prediction markets',
      'should X markets',
      'subjective markets',
      'consensus markets',
      'debate markets',
      'marketplace for opinions',
      'Polymarket alternative',
      'Kalshi alternative',
      'GOAT market',
      'best of all time market',
      'community consensus market',
      'trade your opinion',
      'culture opinion markets',
      'sports opinion markets',
      'music opinion markets',
      'Morfi',
    ],
    appleWebAppTitle: 'Morfi',
  },

  manifest: {
    name: 'Morfi Markets',
    shortName: 'Morfi',
    description:
      'Markets for every debate. Trade your view on opinion markets — the subjective debates no oracle can resolve.',
  },

  pages: {
    fundingDevtools: { title: 'Funding devtools | Morfi' },
    fundingFixtures: { title: 'Funding UI fixtures | Morfi' },
    creatorLogin: { title: 'Creator sign-in | Morfi' },
    livePlayground: { title: 'Live Events UI Playground' },
    settingsProfile: {
      title: 'Settings',
      description: 'Manage your Morfi profile, wallets, connected accounts, and security.',
    },
    workspace: {
      title: 'Workspace',
    },
    authCallback: {
      title: 'Verifying',
    },
    adminProposals: {
      title: 'Admin · Proposals',
    },
    onboarding: {
      title: 'Onboarding',
    },
    debugTrade: {
      title: 'Debug · Trade',
    },
    debugTheme: {
      title: 'Debug · Theme',
    },
    debugSilk: {
      title: 'Debug · Silk Examples',
    },
    debugSuggestedOutcomesPreview: {
      title: 'Debug · Suggested Outcomes Preview',
    },
    notFound: {
      title: 'Page not found',
    },
    privacy: {
      title: 'Privacy Policy | Morfi',
      description:
        'How Morfi collects, uses, and protects your information when you use our opinion-market platform.',
    },
    deleteAccount: {
      title: 'Delete your account | Morfi',
      description:
        'Request deletion of your Morfi account and associated data, or ask us to delete specific personal data.',
    },
    terms: {
      title: 'Terms of Service | Morfi',
      description:
        'The rules that govern your use of Morfi — eligibility, trading, fees, and how markets resolve.',
    },
    risk: {
      title: 'Risk Disclosure | Morfi',
      description:
        'Understand the risks of trading on Morfi — market volatility, settlement risk, and regulatory uncertainty.',
    },
    brand: {
      title: 'Brand kit | Morfi',
      description: 'Morfi logos, type, and color for press and partners.',
    },
    portfolio: {
      title: 'Portfolio | Morfi',
      description:
        'Track your positions, PnL, and full trade history across every Morfi opinion market.',
    },
    tradeShare: {
      title: 'Trade',
    },
  },

  homepage: {
    fallbackTitle: 'Opinion Markets | Trade Your View on Every Debate | Morfi',
    fallbackDescription:
      'Opinion markets for every debate. Prediction markets ask "will X?" — Morfi asks "should X?". Trade your view on the debates no oracle can resolve.',
    dynamicTitle: '{count} opinion markets · {volume} traded | Morfi',
    dynamicDescriptionBase:
      '{count} live opinion markets on Morfi — {volume} traded. Trade your view on the subjective debates no oracle can resolve.',
    dynamicDescriptionTopSuffix: ' Top market: "{title}".',
    topMarketsListName: 'Top opinion markets on Morfi',
    topMarketsListDescription: 'The most-traded active opinion markets on Morfi.',
  },

  marketsListing: {
    fallbackTitle: 'Opinion Markets | Browse Every Debate | Morfi',
    fallbackDescription:
      'Browse live opinion markets on Morfi. "Should X?" debates across culture, sports, music, and more — trade your view, no oracle required.',
    dynamicTitle: 'Opinion markets · {count} live · {volume} traded | Morfi',
    dynamicDescription:
      'Browse {count} live opinion markets on Morfi. {volume} traded across culture, sports, music, and tech. Trade your view on every debate.',
    itemListName: 'Active opinion markets on Morfi',
    itemListDescription: 'Top active opinion markets on Morfi sorted by volume.',
  },

  liveEventsListing: {
    title: 'Live Events | Trade Every Moment | Morfi',
    description:
      'Follow live and upcoming events, back proposed markets, and trade opinions while each event unfolds on Morfi.',
  },

  liveEvent: {
    fallbackTitle: 'Live event | Morfi',
    fallbackDescription: 'Follow this live event and trade its markets on Morfi.',
    dynamicTitle: '{title} | Live event | Morfi',
    dynamicDescription:
      'Follow {title}, explore its markets, and trade opinions as the event unfolds on Morfi.',
  },

  competitionsListing: {
    breadcrumbLabel: 'Trading competitions',
    title: 'Trading Competitions | Morfi',
    description:
      'Explore live and upcoming trading competitions, prize pools, and final standings from concluded competitions on Morfi.',
  },

  competition: {
    fallbackTitle: 'Trading competition | Morfi',
    fallbackDescription: 'Follow this trading competition and climb the standings on Morfi.',
    dynamicTitle: '{title} | Morfi',
    dynamicDescription:
      'Follow {title}. View the live leaderboard, prize pool and trading competition details on Morfi.',
    concludedDescription:
      'View the final standings and prizes for {title}, a concluded trading competition on Morfi.',
    upcomingDescription:
      'Explore the prize pool and schedule for {title}, an upcoming trading competition on Morfi.',
    rankTitle: '#{rank} in {title} | Morfi',
    rankDescription:
      'Ranked #{rank} in {title} with {value}. Follow the standings and trade opinions to climb on Morfi.',
    rankConcludedDescription:
      'Finished #{rank} in {title} with {value}. View the final standings and prizes on Morfi.',
  },

  launchpadListing: {
    fallbackTitle: 'Proposals | Propose New Opinion Markets | Morfi',
    fallbackDescription:
      'Propose, back, and launch new opinion markets on Morfi. Anyone can pitch a debate — the community backs it, then it goes live.',
    dynamicTitle: 'Proposals · {count} proposals · {volume} backed | Morfi',
    dynamicDescription:
      'Back {count} proposals waiting to launch as opinion markets. {volume} committed by the Morfi community. Propose your own debate.',
    itemListName: 'Live proposals on Morfi',
    itemListDescription:
      'Top community-backed proposals waiting to launch as Morfi opinion markets.',
  },

  winnersListing: {
    fallbackTitle: 'Winners | Graduated Opinion Markets | Morfi',
    fallbackDescription:
      'Markets that reached consensus and graduated. Each winning outcome now trades as a live Solana token — track the breakout movers, volume and market cap.',
    itemListName: 'Winning outcomes on Morfi',
    itemListDescription:
      'Graduated winning outcomes now trading as live tokens on Morfi, sorted by volume.',
  },

  socialListing: {
    fallbackTitle: 'Social | Morfi',
    fallbackDescription:
      'See what the crowd thinks. Top opinion markets, sharpest theses, and best-ranked traders on Morfi.',
    dynamicTitle: 'Social · top opinion markets and theses | Morfi',
    dynamicDescription:
      "See the top {count} live opinion markets on Morfi alongside the crowd's sharpest theses. Trade your view on every debate.",
    rankTitle: 'Social | Morfi',
    rankDescription:
      'See where I rank on the Morfi leaderboard — top traders, top theses, top opinion markets.',
    itemListName: 'Top opinion markets on Morfi',
    itemListDescription: 'The most-traded active opinion markets surfaced on Morfi Social.',
  },

  marketDetail: {
    fallbackTitle: 'Opinion market | Morfi',
    emptyOutcomesTitle: 'Opinion market',
    fallbackDescription: 'Trade your view on Morfi — the opinion-market platform for every debate.',
    shareDescription:
      'Trade your view on this opinion market — pick a side on the debate that matters.',
    shareTitleClaim: 'I won on Morfi',
    shareTitlePosition: 'My position on Morfi',
    shareTitlePrizePool: 'Prize pool on Morfi',
    shareTitleResult: 'See how this market resolved on Morfi',
    resolvedDescription:
      'Resolved opinion market: "{title}" — {winner} won. {volume} traded across {outcomeCount} outcomes on Morfi.',
    openDescription:
      '"{title}" — The crowd is {pct}% behind {label}. {volume} traded across {outcomeCount} outcomes on this Morfi opinion market. Trade your view.',
    preInitDescription: 'Trade your view on "{title}" — a live opinion market on Morfi.',
  },

  winnerDetail: {
    fallbackTitle: 'Winning outcome | Morfi',
    fallbackDescription:
      'Trade the winning outcome from a resolved Morfi opinion market — now live as its own tradeable token.',
    title: '{label} won "{market}"',
    titleNoMarket: '{label} · Winning outcome',
    description:
      '{label} won "{market}" on Morfi and now trades as its own token at {price}. Trade the winning outcome.',
    descriptionNoMarket:
      '{label} is a winning outcome on Morfi, now trading at {price}. Trade the winning outcome.',
  },

  profile: {
    titleAnonymous: 'Portfolio on Morfi',
    titleFull: '{name} · #{rank} PnL · {volume} traded',
    titleRankOnly: '{name} · #{rank} on Morfi',
    titleVolumeOnly: '{name} · {volume} traded · Morfi',
    titleNameOnly: '{name} | Morfi',
    descriptionAnonymous:
      'View portfolios on Morfi. See top traders, theses, and live opinion markets.',
    descriptionFull:
      '{name} ranks #{rank} by PnL with {volume} traded on Morfi opinion markets. See their full portfolio.',
    descriptionRankOnly: '{name} ranks #{rank} on the Morfi leaderboard. See their full portfolio.',
    descriptionVolumeOnly:
      '{name} has traded {volume} on Morfi opinion markets. See their full portfolio.',
    descriptionNameOnly:
      "View {name}'s portfolio on Morfi. See positions, PnL, and trading history.",
  },

  proposal: {
    title: 'Proposal | Morfi',
    description: 'Help launch the next opinion market on Morfi.',
    dynamicTitle: '{title} | Proposals | Morfi',
    dynamicDescription:
      'Back this proposal so it can launch as an opinion market on Morfi: "{title}".',
  },

  referral: {
    title: 'Join me on Morfi',
    description:
      "You're invited to Morfi — the opinion-market platform. Trade your view, and you and your friend both earn rewards on every trade.",
    fallbackTitle: 'Morfi Markets',
  },

  about: {
    title: 'About Morfi | Help us reach mainnet',
    description:
      "Morfi is the opinion-market platform. We're crowdfunding our Solana mainnet launch — back the team and unlock founding-trader rewards.",
    /** Live dynamic-title interpolated when the donation balance fetch
     *  succeeds. Placeholders: `{raised}` and `{goal}`. */
    dynamicTitle: 'About Morfi · {raised} of {goal} raised | Morfi',
    /** Live dynamic-description interpolated alongside `dynamicTitle`. */
    dynamicDescription:
      'Morfi is crowdfunding its Solana mainnet launch — {raised} raised of {goal} goal. Contribute SOL or USDC and unlock founding-trader rewards.',
  },

  landing: {
    title: 'Morfi | Opinion markets for every debate',
    description:
      'Trade subjective debates on Morfi. Should X, not Will X — put your money where your mouth is.',
    dynamicTitle: 'Morfi · {raised} of {goal} raised | Opinion markets',
    dynamicDescription:
      'Opinion markets for every debate — {raised} raised of {goal} toward mainnet. Trade your view on Morfi.',
  },

  ogCard: {
    home: {
      eyebrow: null,
      heading: 'Opinion markets for every debate',
      body: 'Trade your view on Morfi. Should X, not Will X.',
    },
    markets: {
      eyebrow: 'OPINION MARKETS',
      heading: 'Browse every debate',
      body: 'Should X? Trade the subjective debates no oracle can resolve.',
    },
    winners: {
      eyebrow: 'WINNERS',
      heading: 'The breakout winners',
      body: 'Outcomes that won their market and now trade as live tokens. Track the movers.',
    },
    about: {
      eyebrow: 'ABOUT',
      heading: 'About Morfi',
      body: 'The opinion-market platform — built in the open. Help us reach mainnet.',
    },
    social: {
      eyebrow: 'SOCIAL',
      heading: 'See what the crowd thinks',
      body: 'Top opinion markets, theses, and traders on Morfi.',
    },
    competitions: {
      eyebrow: 'TRADING COMPETITIONS',
      heading: 'Trade. Compete. Climb.',
      body: 'Explore trading competitions, prize pools and final standings on Morfi.',
    },
    launchpad: {
      eyebrow: 'PROPOSALS',
      heading: 'Propose an opinion market',
      body: 'Pitch a debate. The crowd backs it. It goes live.',
    },
    portfolio: {
      eyebrow: 'PORTFOLIO',
      heading: 'Your positions, your P&L',
      body: 'Every opinion-market trade, every outcome — tracked on Morfi.',
    },
    portfolio_next: {
      eyebrow: 'PORTFOLIO',
      heading: 'Portfolio (preview)',
      body: 'Internal staging redesign. Not indexed.',
    },
    privacy: {
      eyebrow: 'PRIVACY',
      heading: 'Privacy Policy',
      body: 'How Morfi collects, uses, and protects your information.',
    },
    delete_account: {
      eyebrow: 'ACCOUNT DELETION',
      heading: 'Delete your Morfi account',
      body: 'Request deletion of your account and associated data.',
    },
    terms: {
      eyebrow: 'TERMS',
      heading: 'Terms of Service',
      body: 'The rules that govern your use of Morfi.',
    },
    risk: {
      eyebrow: 'RISK',
      heading: 'Risk Disclosure',
      body: 'Understand the risks before you trade on Morfi.',
    },
    brand: {
      eyebrow: 'BRAND',
      heading: 'Brand kit',
      body: 'Marks, type, and color. Use them as given.',
    },
    compare_polymarket: {
      eyebrow: 'COMPARE',
      heading: 'Morfi vs Polymarket',
      body: 'Opinion markets vs prediction markets. Should X vs Will X.',
    },
    compare_kalshi: {
      eyebrow: 'COMPARE',
      heading: 'Morfi vs Kalshi',
      body: 'Opinion markets vs event contracts. Should X vs Will X.',
    },
    learn_index: {
      eyebrow: 'LEARN',
      heading: 'Learn about opinion markets',
      body: 'The category Morfi created.',
    },
    learn_opinion_markets: {
      eyebrow: 'LEARN',
      heading: 'What is an opinion market?',
      body: 'Trade your view on debates no oracle can resolve.',
    },
    learn_opinion_vs_prediction: {
      eyebrow: 'LEARN',
      heading: 'Opinion vs prediction markets',
      body: 'Should X versus Will X. Two different categories.',
    },
    learn_bonding_curve: {
      eyebrow: 'LEARN',
      heading: 'How bonding curves price opinions',
      body: 'On-chain pricing with liquidity from day one.',
    },
    learn_how_morfi_resolves: {
      eyebrow: 'LEARN',
      heading: 'How Morfi markets resolve',
      body: 'The market decides the winner — no referee.',
    },
    learn_graduated_tokens: {
      eyebrow: 'LEARN',
      heading: 'What happens when a market wins',
      body: 'The winning side graduates into its own tradable token.',
    },
    learn_prize_pool: {
      eyebrow: 'LEARN',
      heading: 'How you get paid',
      body: 'Winners split the prize pool by how much they hold.',
    },
    learn_launchpad: {
      eyebrow: 'LEARN',
      heading: 'How a market is born',
      body: 'Anyone proposes a debate. The crowd backs it live.',
    },
    learn_is_gambling: {
      eyebrow: 'LEARN',
      heading: 'Is opinion trading gambling?',
      body: 'You back a view with money and prices move. Know the risk.',
    },
    compare_manifold: {
      eyebrow: 'COMPARE',
      heading: 'Morfi vs Manifold',
      body: 'Real money on every debate vs play-money forecasting.',
    },
    compare_limitless: {
      eyebrow: 'COMPARE',
      heading: 'Morfi vs Limitless',
      body: 'Opinion markets for every debate vs fast event markets.',
    },
    compare_pumpfun: {
      eyebrow: 'COMPARE',
      heading: 'Morfi vs pump.fun',
      body: 'Back a side, then it graduates to a token — debates, not just coins.',
    },
    markets_category: {
      eyebrow: 'MARKETS',
      heading: 'Browse opinion markets by topic',
      body: 'Back your view on the debates that matter to you.',
    },
  },

  ogDefault: {
    headingPrimaryLine1: 'Opinion markets',
    headingPrimaryLine2: 'for every debate',
    headingSecondary: 'Put your money where your mouth is',
  },

  webApplication: {
    name: 'Morfi Markets',
    alternateName: 'Morfi',
    browserRequirements: 'Requires JavaScript and a modern browser',
    features: [
      'Opinion markets for every debate',
      'Should-X questions only opinion markets can answer',
      'Multi-outcome markets with on-chain pricing from day one',
      'Community consensus resolution — no external oracle',
      'Anyone can start a debate via Proposals',
      '21 supported languages',
    ],
  },

  compare: {
    polymarket: {
      title: 'Morfi vs Polymarket | Opinion Markets vs Prediction Markets',
      description:
        'Polymarket asks "will X happen?" with an oracle. Morfi asks "should X?" with the crowd. Compare the two side by side.',
      h1: 'Morfi vs Polymarket',
    },
    kalshi: {
      title: 'Morfi vs Kalshi | Opinion Markets vs Event Contracts',
      description:
        'Kalshi is CFTC-regulated, sports-dominated, binary-only. Morfi is opinion-first, multi-outcome, anyone-can-launch. Compare the two.',
      h1: 'Morfi vs Kalshi',
    },
    manifold: {
      title: 'Morfi vs Manifold | Real Money vs Play Money',
      description:
        'Manifold runs on play money. Morfi puts real money behind every debate, with winners paid from a prize pool. Compare the two side by side.',
      h1: 'Morfi vs Manifold',
    },
    limitless: {
      title: 'Morfi vs Limitless | Opinion Markets vs Event Markets',
      description:
        'Limitless lists fast yes/no event markets. Morfi hosts subjective debates the crowd decides, then graduates winners into tokens. Compare both.',
      h1: 'Morfi vs Limitless',
    },
    pumpfun: {
      title: 'Morfi vs pump.fun | Debates That Become Tokens',
      description:
        'pump.fun launches memecoins. Morfi launches debates — back a side, the crowd decides a winner, and that winning side graduates into a token.',
      h1: 'Morfi vs pump.fun',
    },
  },

  category: {
    titleTemplate: '{category} Opinion Markets | Morfi',
    descriptionTemplate:
      'Back your view on live {category} debates. {count} markets, {volume} traded — the crowd decides the winner, no referee. Trade on Morfi.',
    fallbackDescriptionTemplate:
      'Back your view on {category} debates on Morfi. The crowd decides the winner — no referee, no real-world event to wait on.',
    itemListNameTemplate: 'Live {category} opinion markets on Morfi',
    itemListDescriptionTemplate:
      'The most-backed live {category} opinion markets on Morfi, sorted by volume.',
  },

  ogCardDynamic: {
    backerRewards: {
      title: 'Backer rewards on Morfi',
      description: 'Rewards earned by backing opinion markets on Morfi.',
      eyebrow: 'Backer rewards',
      totalEarned: 'Total earned',
      marketBacked: '{count} market backed',
      marketsBacked: '{count} markets backed',
    },
    shared: {
      ctaTradeViewMorfi: 'Trade your view on Morfi →',
      ctaTradeViewOpinion: 'Trade your view on opinion markets →',
      ctaTradeViewOpinionPeriod: 'Trade your view on opinion markets.',
      fallbackAuthorMorfian: 'A Morfian',
      fallbackAuthorAnonymous: 'Anonymous',
      directionBought: 'Bought',
      directionSold: 'Sold',
      directionSwapped: 'Swapped',
      onPrefix: 'on',
      outcomeFallback: 'Outcome',
      outcomeIndexed: 'Outcome {index}',
    },
    claim: {
      heading: 'I won on Morfi',
      cta: 'Trade your view on Morfi →',
      eyebrow: 'Prize claimed',
      shellEyebrow: 'Winnings',
      wonByPrefix: 'Won by',
    },
    gift: {
      eyebrow: 'Gift',
      fallbackHeading: 'You got a Morfi gift',
      fallbackCta: 'Sign in to claim →',
      sentLabel: 'sent',
      toLabel: 'to',
      claimedLabel: 'Claimed',
      fallbackSenderLabel: 'Anonymous Morfian',
    },
    giftThank: {
      eyebrow: 'Gift thank',
      headingLine1: 'Just claimed a',
      headingLine2: 'Morfi gift',
      cta: 'Thank a Morfian →',
    },
    globe: {
      eyebrow: 'Morfi · Globe',
      subtitle: 'Live opinion markets, mapped by location.',
      market: 'Markets across the globe',
      launch: 'Proposals across the globe',
      winner: 'Winners across the globe',
      users: 'Traders across the globe',
    },
    market: {
      eyebrowResolved: 'Resolved',
      eyebrowCancelled: 'Cancelled',
      eyebrowLive: 'Opinion',
      fallbackHeading: 'A Morfi opinion market',
      fallbackCta: 'Tap to trade your view →',
      wonByPrefix: 'Won by',
      resolvesAtPrefix: 'Resolves at',
      resolvedFooterLabel: 'Resolved',
      tradedSuffix: '{count} Traded',
      cancelledLabel: 'Cancelled',
      otherOpinionSingular: 'other opinion',
      otherOpinionPlural: 'other opinions',
    },
    marketPrizePool: {
      shellEyebrow: 'Morfi · prize pool',
      heroLabel: 'Prize pool',
      fallbackHeading: 'A Morfi opinion market',
      fallbackCta: 'Tap to trade your view →',
    },
    marketResult: {
      shellEyebrow: 'Market resolved',
      youWonLabel: 'You won',
      youLostLabel: 'You lost',
      evenLabel: 'Broke even',
      winnerEyebrow: 'Winner',
      wonByPrefix: 'Won by',
      neutralHeading: 'Market resolved on Morfi',
      fallbackHeading: 'A Morfi opinion market',
      fallbackCta: 'Tap to trade your view →',
    },
    marketResultPublic: {
      shellEyebrow: 'Market resolved',
      winnerEyebrow: 'Winner',
      finalOddsLabel: 'Final odds',
      prizePoolLabel: 'Prize pool',
      volumeLabel: 'Volume',
      fallbackHeading: 'A Morfi opinion market',
      fallbackCta: 'Tap to trade your view →',
    },
    pnlMarket: {
      eyebrowCancelled: 'Cancelled',
      eyebrowResolved: 'Resolved',
      eyebrowLive: 'Position',
      fallbackHeading: 'My Morfi position',
      fallbackCta: 'Trade your view on Morfi →',
      totalRealizedPnl: 'Total Realized PnL',
      totalPnl: 'Total PnL',
      positionsLabel: 'Positions',
      otherOutcomeSingular: 'other outcome',
      otherOutcomePlural: 'other outcomes',
      investedSuffix: '{value} invested',
      tradeSingular: 'trade',
      tradePlural: 'trades',
      liveQuoteUnavailable: 'Live valuation unavailable',
      unavailableLabel: 'Unavailable',
    },
    pnlPosition: {
      eyebrowCancelled: 'Cancelled',
      eyebrowResolved: 'Resolved',
      eyebrowLive: 'Position',
      fallbackHeading: 'My Morfi position',
      fallbackCta: 'Trade your view on Morfi →',
      realizedPnl: 'Realized PnL',
      unrealizedPnl: 'Unrealized PnL',
      positionLabel: 'Position',
      entryLabel: 'Entry',
      settledLabel: 'Settled',
      spotPriceLabel: 'Spot price',
      liveQuoteUnavailable: 'Live quote unavailable',
      unavailableLabel: 'Unavailable',
    },
    profile: {
      eyebrow: 'Profile',
      fallbackHeading: 'A Morfi trader',
      fallbackCta: 'Trade your view on Morfi →',
      joinedPrefix: 'Joined',
      portfolioValue: 'Portfolio value',
      lastDayLabel: 'last day',
      winRate: 'Win rate',
      trades: 'Trades',
      volume: 'Volume',
      topPosition: 'Top settled position',
      marketsSuffix: '{count} markets',
      allTimeLabel: 'all-time',
    },
    proposal: {
      eyebrowGraduated: 'Market live',
      eyebrowRejected: 'Rejected',
      eyebrowExpired: 'Expired',
      eyebrowLive: 'Proposal',
      fallbackHeading: 'A Morfi proposal',
      fallbackCta: 'Help launch the next opinion market →',
      funded: 'Funded',
      fundedPercent: 'Funded · {pct}%',
      percentFunded: '{pct}% funded',
      graduated: 'Graduated',
      rejected: 'Rejected',
      closesIn: 'Closes in {time}',
      closed: 'Closed',
      ofGoal: 'of goal',
      asOfPrefix: 'As of',
      backerSingular: 'backer',
      backerPlural: 'backers',
    },
    proposalBack: {
      eyebrowBacked: 'Backed',
      eyebrowLive: 'Proposal',
      fallbackHeading: 'Backed a Morfi proposal',
      fallbackCta: 'Help launch the next opinion market →',
    },
    rank: {
      eyebrow: 'Rank',
      fallbackHeading: 'Climb the leaderboard',
      fallbackCta: 'Trade your view on Morfi →',
      realisedPnl: 'Realised PnL',
      winRate: 'Win rate',
      volume: 'Volume',
      marketsTradedOne: '1 market traded',
      marketsTradedMany: '{count} markets traded',
      totalPnl: 'Total P&L',
      pnlRank: 'P&L Rank',
      volumeRank: 'Volume Rank',
    },
    rankBoosters: {
      eyebrow: 'Boosters rank',
      fallbackHeading: 'Climb the boosters leaderboard',
      fallbackCta: 'Boost a market on Morfi →',
      totalAmount: 'Total Boosted',
      numBoosts: 'Boosts',
      marketsBoosted: 'Markets Boosted',
      amountRank: 'Amount Rank',
      boostsRank: 'Boosts Rank',
      marketsBoostedRank: 'Markets Rank',
    },
    rankCreators: {
      eyebrow: 'Creators rank',
      fallbackHeading: 'Climb the creators leaderboard',
      fallbackCta: 'Propose a market on Morfi →',
      totalVolume: 'Total Volume',
      marketsCreated: 'Markets Created',
      topMarketVolume: 'Top Market Volume',
      topMarketRank: 'Top Market Rank',
      marketsCreatedRank: 'Markets Rank',
      totalVolumeRank: 'Volume Rank',
    },
    rankBackers: {
      eyebrow: 'Backers rank',
      fallbackHeading: 'Climb the backers leaderboard',
      fallbackCta: 'Back a market on Morfi →',
      totalBacked: 'Total Backed',
      numBacks: 'Backs',
      amountRank: 'Amount Rank',
      backsRank: 'Backs Rank',
      topMarketRank: 'Top Market Rank',
    },
    streak: {
      eyebrow: 'Streak',
      dayStreakLabel: 'Day streak',
      levelLabel: 'Level {level} of 7',
      longestLabel: 'Longest',
      daysToNext: '{count} days to {tier}',
      daysToNextOne: '{count} day to {tier}',
      maxTierLabel: 'Max tier reached',
      noStreakLabel: 'No streak yet',
      startLabel: 'Start your streak on Morfi',
      fallbackHeading: 'Build your trading streak',
      fallbackCta: 'Trade your view on Morfi →',
    },
    referral: {
      eyebrow: 'Referral',
      fallbackHeading: 'Join me on Morfi',
      fallbackCta: 'Trade your view — both earn rewards on every trade →',
      invitedSuffix: 'invited you to Morfi',
      tagline: 'Trade your view on opinion markets',
      rewardChip: 'Both earn rewards on every trade',
      fallbackInviter: 'A friend',
    },
    supportMainnet: {
      eyebrow: 'Public devnet',
      headingPrimary: 'Opinion markets for every debate.',
      headingSecondary: '$2.5M raised. Backed by Pharsalus, Yellow, and the Solana Foundation.',
    },
    rewardsRank: {
      eyebrow: 'MORFI QUESTS',
      rankEyebrow: 'QUESTS RANK',
    },
    thesis: {
      eyebrow: 'Thesis',
      fallbackQuote: '“A take, with money behind it.”',
      fallbackCta: 'Trade your view on opinion markets →',
    },
    trade: {
      eyebrow: 'Trade',
      fallbackHeading: 'Just traded on Morfi',
      fallbackCta: 'Trade your view on opinion markets →',
    },
    winner: {
      eyebrow: 'Winner',
      fallbackHeading: 'A winning outcome on Morfi',
      fallbackBody: 'Trade the winner from a resolved opinion market.',
      wonMarketLabel: 'Won the market',
      marketCapLabel: 'Marketcap',
      volumeTotalLabel: 'Total volume',
      holdersLabel: 'Holders',
      change24hLabel: '24h change',
      priceLabel: 'Price',
    },
  },

  learn: {
    index: {
      title: 'Learn Opinion Markets | Morfi',
      description:
        'How opinion markets work — back a side, prices move, the crowd decides the winner, and winners share a prize pool. Plain-English guides.',
    },
    opinionMarkets: {
      title: 'What Is an Opinion Market? | Morfi',
      description:
        'An opinion market lets you back your view on a debate. Instead of "will X happen?", it asks "which side does the crowd back?". Here is how it works.',
    },
    opinionVsPrediction: {
      title: 'Opinion Markets vs Prediction Markets | Morfi',
      description:
        'Prediction markets wait for a real-world result. Opinion markets let the crowd decide the winner — no referee. Compare both side by side.',
    },
    bondingCurve: {
      title: 'How Prices Move on Morfi | Bonding Curve Explained | Morfi',
      description:
        'On Morfi, backing a side pushes its price up and selling pushes it down — with liquidity from day one. Here is how market prices move, in plain English.',
    },
    howMorfiResolves: {
      title: 'How Opinion Markets Resolve | No Referee | Morfi',
      description:
        'A Morfi market decides its own winner — when one side holds the lead long enough, the market agrees. No oracle, no referee. Here is how resolution works.',
    },
    graduatedTokens: {
      title: 'Graduation: When a Market Wins | Morfi',
      description:
        'When a Morfi market settles, the winning side graduates into its own tradable token with a live price. Here is what graduation means and why it matters.',
    },
    prizePool: {
      title: 'How You Get Paid on Morfi | The Prize Pool | Morfi',
      description:
        'A small fee on every trade fills a prize pool. When a market settles, winners split it by how much of the winning side they hold. Here is how payouts work.',
    },
    launchpad: {
      title: 'Proposals: How a Market Is Born | Morfi',
      description:
        'Anyone can propose a debate on Morfi. The community backs the ones it wants, and a proposal goes live as a market on its own. Here is how Proposals work.',
    },
    isGambling: {
      title: 'Is Opinion Trading Gambling? | Morfi',
      description:
        'You back a view with money and prices move, so treat it with the same care. Here is an honest look at the risk of opinion trading — and how Morfi works.',
    },
    hub: {
      itemListName: 'Learn about opinion markets',
      itemListDescription:
        'Plain-English guides to opinion markets — what they are, how prices move, how markets resolve, getting paid, graduation, and Proposals.',
      entries: {
        opinionMarkets: {
          title: 'What is an opinion market?',
          description:
            'Back your view on a debate. The crowd decides the winner — not a referee. Start here.',
        },
        opinionVsPrediction: {
          title: 'Opinion markets vs prediction markets',
          description:
            'One waits for a real-world result; the other lets the crowd decide. Two different categories.',
        },
        bondingCurve: {
          title: 'How prices move',
          description:
            'Backing a side pushes its price up; selling pushes it down — with liquidity from day one.',
        },
        howMorfiResolves: {
          title: 'How markets resolve',
          description:
            'When one side holds the lead long enough, the market agrees on a winner. No referee.',
        },
        graduatedTokens: {
          title: 'Graduation: when a market wins',
          description:
            'The winning side becomes its own tradable token with a live price you can keep holding.',
        },
        prizePool: {
          title: 'How you get paid',
          description:
            'Winners split a prize pool by how much of the winning side they hold when a market settles.',
        },
        launchpad: {
          title: 'How a market is born',
          description:
            'Anyone proposes a debate; the community backs it; it goes live on its own. No gatekeepers.',
        },
        isGambling: {
          title: 'Is opinion trading gambling?',
          description:
            'An honest look at the risk: you back a view with money, and prices can fall as well as rise.',
        },
        comparePolymarket: {
          title: 'Morfi vs Polymarket',
          description:
            'Should-X debates the crowd decides vs will-X events settled by a referee. When each fits.',
        },
        compareKalshi: {
          title: 'Morfi vs Kalshi',
          description:
            'Opinion markets vs CFTC-regulated event contracts. When each platform is the right venue.',
        },
        compareManifold: {
          title: 'Morfi vs Manifold',
          description:
            'Real money on every debate, with prize-pool payouts, vs play-money forecasting.',
        },
        compareLimitless: {
          title: 'Morfi vs Limitless',
          description:
            'Subjective debates the crowd decides, with graduation, vs fast yes/no event markets.',
        },
        comparePumpfun: {
          title: 'Morfi vs pump.fun',
          description:
            'Back a side of a debate that can graduate into a token — not just launching coins.',
        },
      },
    },
  },
};

export default copy;
